/**
 * Shared helper for resolving the best-available profile photo URL for a
 * social media channel from its metadata object.
 *
 * Different platforms and migration sources store the avatar URL under
 * different keys. This function checks all known variants in priority order
 * (signed S3 URL first, then platform-specific originals as fallback) and
 * returns the first truthy string it finds, or null if none exists.
 *
 * Priority:
 *   avatarUrl            – signed S3 URL generated on channel fetch (newest)
 *   profilePicture       – LinkedIn, Instagram, Facebook (migrated)
 *   profilePictureUrl    – Threads
 *   profile_picture_url  – some platforms (snake_case variant)
 *   picture              – Facebook user-token responses, Google Business
 *   profileImage         – Pinterest
 *   profileImageUrl      – camelCase variant
 *   profile_image_url    – snake_case variant
 *   thumbnailUrl         – YouTube
 *   avatar               – Bluesky (from profile)
 *   originalAvatar       – Bluesky / pre-S3-rehost migration
 *   originalProfilePicture – pre-S3-rehost migration
 *   image                – generic fallback
 *   siteIconUrl           – WordPress Site Icon / favicon
 *   logo                 – Google My Business, LinkedIn organisation
 */

export function getChannelAvatar(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  if (!metadata) return null;

  return (
    (metadata.avatarUrl as string) ||
    (metadata.profilePicture as string) ||
    (metadata.profilePictureUrl as string) ||
    (metadata.profile_picture_url as string) ||
    (metadata.picture as string) ||
    (metadata.profileImage as string) ||
    (metadata.profileImageUrl as string) ||
    (metadata.profile_image_url as string) ||
    (metadata.thumbnailUrl as string) ||
    (metadata.avatar as string) ||
    (metadata.originalAvatar as string) ||
    (metadata.originalProfilePicture as string) ||
    (metadata.image as string) ||
    (metadata.siteIconUrl as string) ||
    (metadata.logo as string) ||
    null
  );
}

/**
 * Convenience wrapper that accepts a full Channel object from either
 * `@/services/channels` or `@/services/channels.service`.
 *
 * `metadata` on both Channel types is compatible with
 * `Record<string, unknown> | null | undefined`.
 */
export function getChannelAvatarFromChannel(channel: {
  metadata?: Record<string, unknown> | null | { [key: string]: unknown } | undefined;
}): string | null {
  return getChannelAvatar(channel.metadata as Record<string, unknown> | null | undefined);
}
